import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  BASE_CURRENCY,
  CURRENCIES,
  CURRENCY_STORAGE_KEY,
  FALLBACK_RATES_FROM_RWF,
} from '../constants/currency'

const CurrencyContext = createContext(null)

function readStoredCurrency() {
  try {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY)
    if (saved && CURRENCIES.some((c) => c.code === saved)) return saved
  } catch {
    /* ignore */
  }
  return BASE_CURRENCY
}

async function fetchRatesFromRwf() {
  const urls = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/rwf.json',
    'https://latest.currency-api.pages.dev/v1/currencies/rwf.json',
  ]

  for (const url of urls) {
    try {
      const res = await fetch(url)
      if (!res.ok) continue
      const data = await res.json()
      const table = data?.rwf || data?.RWF
      if (!table || typeof table !== 'object') continue

      const rates = { RWF: 1 }
      for (const { code } of CURRENCIES) {
        if (code === 'RWF') continue
        const key = code.toLowerCase()
        const value = Number(table[key])
        if (Number.isFinite(value) && value > 0) {
          rates[code] = value
        }
      }
      return rates
    } catch {
      /* try next */
    }
  }
  return null
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(readStoredCurrency)
  const [rates, setRates] = useState(FALLBACK_RATES_FROM_RWF)
  const [ratesUpdatedAt, setRatesUpdatedAt] = useState(null)
  const [loadingRates, setLoadingRates] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoadingRates(true)
      const live = await fetchRatesFromRwf()
      if (cancelled) return
      if (live) {
        setRates({ ...FALLBACK_RATES_FROM_RWF, ...live })
        setRatesUpdatedAt(new Date())
      }
      setLoadingRates(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const setCurrency = useCallback((code) => {
    const next = CURRENCIES.some((c) => c.code === code) ? code : BASE_CURRENCY
    setCurrencyState(next)
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  const convertFromRwf = useCallback(
    (amountRwf) => {
      const n = Number(amountRwf) || 0
      const rate = rates[currency] ?? FALLBACK_RATES_FROM_RWF[currency] ?? 1
      return n * rate
    },
    [currency, rates]
  )

  const formatPrice = useCallback(
    (amountRwf, { showCode = true } = {}) => {
      const meta = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0]
      const converted = convertFromRwf(amountRwf)
      const digits = currency === 'RWF' || currency === 'UGX' || currency === 'KES' ? 0 : 2

      try {
        const formatted = new Intl.NumberFormat(meta.locale || 'en-US', {
          style: 'currency',
          currency: meta.code,
          currencyDisplay: 'symbol',
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(converted)

        // Some locales render RWF oddly; keep a clear label
        if (currency === 'RWF') {
          return `RWF ${Math.round(Number(amountRwf) || 0).toLocaleString('en-US')}`
        }
        return showCode ? formatted : formatted
      } catch {
        const n = converted.toLocaleString('en-US', {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })
        return `${meta.symbol} ${n}`
      }
    },
    [currency, convertFromRwf]
  )

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      currencies: CURRENCIES,
      rates,
      ratesUpdatedAt,
      loadingRates,
      convertFromRwf,
      formatPrice,
      baseCurrency: BASE_CURRENCY,
      isForeign: currency !== BASE_CURRENCY,
    }),
    [currency, setCurrency, rates, ratesUpdatedAt, loadingRates, convertFromRwf, formatPrice]
  )

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext)
  if (!ctx) {
    throw new Error('useCurrency must be used within CurrencyProvider')
  }
  return ctx
}

export default CurrencyContext
